import { useLayoutEffect, useRef, useState, type ClipboardEvent } from 'react';
import { Cross1Icon, Pencil2Icon } from '@radix-ui/react-icons';
import {
  formatNoteRange,
  noteFormatRange,
  noteRangeIsBold,
  type ClassroomNoteFormat,
} from '../domain/classroom-note-format';
import { FOX_REFLECTION_MAX_LENGTH } from '../domain/fox-kifu-reflection';

interface FoxKifuReflectionCardProps {
  readonly initialHtml: string;
  readonly recordLabel: string;
  readonly onChange: (html: string) => void;
  readonly onClose: () => void;
}

const TEXT_COLORS = [
  ['黑色', '#000000'],
  ['红色', '#b53a32'],
  ['橙色', '#b8611b'],
  ['绿色', '#2f7357'],
  ['蓝色', '#2f679b'],
] as const;
const HIGHLIGHTS = [
  ['无颜色', 'transparent'],
  ['黄色', '#ffe58a'],
  ['红色', '#f7b6ac'],
  ['蓝色', '#bcdaf4'],
] as const;

/** Mount with recordId as key: restoring one record must not move another record's caret. */
export function FoxKifuReflectionCard({
  initialHtml,
  recordLabel,
  onChange,
  onClose,
}: FoxKifuReflectionCardProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const selectionRef = useRef<Range | null>(null);
  const initialRef = useRef(initialHtml);
  const acceptedHtml = useRef(initialHtml);
  const composing = useRef(false);
  const [bold, setBold] = useState(false);
  const [color, setColor] = useState('#000000');
  const [highlight, setHighlight] = useState('transparent');
  const [fontSize, setFontSize] = useState('20');
  const [error, setError] = useState<string | null>(null);

  useLayoutEffect(() => {
    // Drafts originate only in this editor. Paste/drop cannot import external HTML.
    if (editorRef.current) editorRef.current.innerHTML = initialRef.current;
  }, []);

  const rememberSelection = () => {
    const editor = editorRef.current;
    const selection = document.getSelection();
    if (!editor || !selection?.rangeCount) return;
    const range = selection.getRangeAt(0);
    if (!editor.contains(range.commonAncestorContainer)) return;
    selectionRef.current = range.cloneRange();
    setBold(noteRangeIsBold(editor, noteFormatRange(editor, range)));
  };

  const commit = () => {
    const editor = editorRef.current;
    if (!editor || composing.current) return;
    if ((editor.textContent ?? '').length > FOX_REFLECTION_MAX_LENGTH) {
      editor.innerHTML = acceptedHtml.current;
      selectionRef.current = null;
      setError(`最多可记录 ${FOX_REFLECTION_MAX_LENGTH} 字，超出部分未添加。`);
      return;
    }
    acceptedHtml.current = editor.innerHTML;
    onChange(editor.innerHTML);
    setError(null);
    rememberSelection();
  };

  const applyFormat = (format: ClassroomNoteFormat) => {
    const editor = editorRef.current;
    const selection = document.getSelection();
    if (!editor || !selection || composing.current) return;
    const range = noteFormatRange(editor, selectionRef.current);
    if (range.collapsed) return;
    const result = formatNoteRange(editor, range, format);
    editor.focus({ preventScroll: true });
    selection.removeAllRanges();
    selection.addRange(result);
    selectionRef.current = result.cloneRange();
    commit();
  };

  const pasteText = (event: ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault();
    const editor = editorRef.current;
    const selection = document.getSelection();
    if (!editor || !selection) return;
    const range = selection.rangeCount ? selection.getRangeAt(0) : document.createRange();
    if (!editor.contains(range.commonAncestorContainer)) {
      range.selectNodeContents(editor);
      range.collapse(false);
    }
    const text = event.clipboardData.getData('text/plain');
    if (
      (editor.textContent ?? '').length - range.toString().length + text.length >
      FOX_REFLECTION_MAX_LENGTH
    ) {
      setError(`最多可记录 ${FOX_REFLECTION_MAX_LENGTH} 字，请缩短粘贴内容。`);
      return;
    }
    range.deleteContents();
    const node = document.createTextNode(text);
    range.insertNode(node);
    range.setStartAfter(node);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
    commit();
  };

  return (
    <section className="fox-reflection-card" aria-label="心得体会">
      <header className="fox-reflection-heading">
        <h2>
          <Pencil2Icon aria-hidden="true" />
          心得体会
        </h2>
        <button
          type="button"
          className="fox-reflection-close"
          aria-label="关闭心得体会"
          onClick={onClose}
        >
          <Cross1Icon aria-hidden="true" />
          <span>关闭</span>
        </button>
      </header>
      <p className="fox-reflection-record">{recordLabel}</p>
      <div
        className="fox-reflection-format"
        role="toolbar"
        aria-label="心得体会文字格式"
        onPointerDownCapture={rememberSelection}
      >
        <button
          type="button"
          aria-label="加粗"
          aria-pressed={bold}
          onPointerDown={(event) => event.preventDefault()}
          onClick={() => {
            const editor = editorRef.current;
            if (editor)
              applyFormat({
                fontWeight: noteRangeIsBold(editor, noteFormatRange(editor, selectionRef.current))
                  ? '400'
                  : '700',
              });
          }}
        >
          B
        </button>
        <label>
          字体颜色
          <select
            aria-label="字体颜色"
            value={color}
            onChange={(event) => {
              setColor(event.target.value);
              applyFormat({ color: event.target.value });
            }}
          >
            {TEXT_COLORS.map(([label, value]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label>
          突出显示
          <select
            aria-label="文本突出显示颜色"
            value={highlight}
            onChange={(event) => {
              setHighlight(event.target.value);
              applyFormat({ backgroundColor: event.target.value });
            }}
          >
            {HIGHLIGHTS.map(([label, value]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label>
          字号
          <select
            aria-label="字体大小"
            value={fontSize}
            onChange={(event) => {
              setFontSize(event.target.value);
              applyFormat({ fontSize: `${event.target.value}px` });
            }}
          >
            {[13, 16, 18, 20, 22, 24, 30, 32].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
      </div>
      {error ? (
        <p className="fox-reflection-error" role="alert">
          {error}
        </p>
      ) : null}
      <div
        ref={editorRef}
        className="fox-reflection-editor"
        role="textbox"
        aria-label="心得体会内容"
        aria-multiline="true"
        aria-describedby="fox-reflection-retention"
        contentEditable
        suppressContentEditableWarning
        spellCheck={false}
        data-placeholder="点击这里，记下这盘棋的收获与思考…"
        onInput={commit}
        onPaste={pasteText}
        onDrop={(event) => event.preventDefault()}
        onCompositionStart={() => {
          composing.current = true;
        }}
        onCompositionEnd={() => {
          composing.current = false;
          commit();
        }}
        onKeyUp={rememberSelection}
        onPointerUp={rememberSelection}
        onBlur={rememberSelection}
      />
      <p className="fox-reflection-retention" id="fox-reflection-retention">
        按棋谱在本页暂存 · 关闭不会清空 · 刷新或离开页面后清除
      </p>
    </section>
  );
}
